import React, { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";
import {
  AsyncTableView,
  ATVTemplateToken,
} from "src/pages/EntityFiltersPage/AsyncTableView";
import { ZEntityRow } from "src/pages/EntityFiltersPage/EntityList/types";
import { InputSearch } from "src/pages/EntityFiltersPage/EntityList/InputSearch";
import { Spin } from "antd";
import { onError } from "src/common/onError";
import { ZChildEntities } from "src/common/attrEdit/components/ZChildEntities";
import { t } from "i18next";
import { classNames } from "src/common/classNames";
import { DefaultViewButton } from "src/pages/EntityFiltersPage/EntityList/DefaultViewButton";
import { ZUid } from "src/types/zUid";
import { ColumnsSettings } from "../tables/ColumnsSettings";
import {
  bindEventOnStorage,
  createStorageEventAction,
  unbindEventOnStorage,
} from "../../common/storageEventUtils";
import styles from "./ChildEntities.module.less";
import { ChildEntitiesStore, ZEntityFiltersChild } from "./ChildEntitiesStore";
import { ChildEntitiesActions } from "./ChildEntitiesActions";
import { SummaryFooter } from "./SummaryFooter";
import { ChildEntsActionType } from "./ChildEntities.types";
import { selectOnRowClick } from "../tables/selectOnRowClick";
import { ChildEntitiesCreators } from "./ChildEntitiesCreators";
import { EntityEditorModal } from "../EntityEditor";
import { getChildEntitiesRestriction } from "./childEntitiesRestriction";

type PropsChildEntities = {
  parentObjId?: ZUid | null;
  childAttributeId?: ZUid;
  editorInfo: ZChildEntities | undefined;
  disabled?: boolean;
  entityId?: ZUid;
  childDictionaryAttributeId?: ZUid | null;
  childDictionaryValueId?: ZUid | null;
  outerStore?: ChildEntitiesStore;
  disableResize?: boolean;
};

export const ChildEntities: React.FC<PropsChildEntities> = observer(
  ({
    parentObjId,
    childAttributeId,
    editorInfo,
    disabled,
    entityId,
    childDictionaryAttributeId,
    childDictionaryValueId,
    outerStore,
    disableResize,
  }) => {
    const [isHeightLimit, setIsHeightLimit] = useState<boolean>(false);
    const tableRef = useRef<HTMLDivElement | null>(null);
    const docHeight = document.documentElement.clientHeight;
    useEffect(() => {
      const element = tableRef?.current;
      if (!element || disableResize) return undefined;
      const resizeObserver = new ResizeObserver(() => {
        if (element.scrollHeight / docHeight > 0.6) {
          setIsHeightLimit(true);
        }
      });
      resizeObserver.observe(element);
      return () => {
        resizeObserver.disconnect();
      };
    }, [tableRef?.current]);

    const navigate = useNavigate();
    const store = useMemo(() => outerStore ?? new ChildEntitiesStore(), []);
    const init = async () => {
      if (!entityId || !parentObjId) return;
      try {
        await store.init(
          parentObjId,
          entityId,
          editorInfo?.attrId,
          childAttributeId,
          editorInfo?.summaryAttrs,
          editorInfo?.buttons,
          getChildEntitiesRestriction({
            childDictionaryAttributeId,
            childDictionaryValueId,
          }),
        );
      } catch (error) {
        onError(error);
      }
    };

    const { tableStore, avalibleActionsSet, defTSettingsStore } = store;

    const onRow = (row: ZEntityRow) => {
      if (avalibleActionsSet.has(ChildEntsActionType.rowClick)) {
        if (editorInfo?.editable) {
          store.openEdit(row.id);
        } else {
          navigate(makeUrl(PageUrl.entityCard, { id: row.id }));
        }
        return;
      }

      if (avalibleActionsSet.has(ChildEntsActionType.select)) {
        if (!tableStore) return;
        selectOnRowClick(row, tableStore);
      }
    };

    useEffect(() => {
      init();
    }, [
      entityId,
      parentObjId,
      childAttributeId,
      editorInfo?.attrId,
      childDictionaryAttributeId,
      childDictionaryValueId,
    ]);

    useEffect(() => {
      const handleReloadTable = createStorageEventAction(
        "lastUpdatedObject",
        (e: StorageEvent) => {
          try {
            const objId = JSON.parse(e.newValue || '""');
            if (objId === parentObjId) {
              store.tableStore?.reload();
            }
          } catch (error) {
            onError(error);
          }
        },
      );
      bindEventOnStorage(handleReloadTable);
      return () => unbindEventOnStorage(handleReloadTable);
    }, []);

    if (!entityId || !editorInfo)
      return <div>{t("Display available only for created instances")}</div>;

    if (!tableStore) return <Spin spinning>{t("Loading")}</Spin>;

    const { subCardStore } = store;
    const useFooter = !!editorInfo.summaryAttrs?.length;
    const useSelection = avalibleActionsSet.has(ChildEntsActionType.select);
    const childEntsTemplateToken: ATVTemplateToken = {
      footer() {
        return <SummaryFooter store={store} />;
      },
    };

    return (
      <div className={styles.box}>
        <div className={styles.header}>
          <div className={styles.search}>
            <InputSearch store={tableStore} disabled={disabled} />
          </div>
          <div className={styles.toolbar}>
            <ColumnsSettings
              store={tableStore}
              extraButtons={
                defTSettingsStore?.displayAttrs ? (
                  <DefaultViewButton
                    onClick={() => defTSettingsStore?.applySettings(true)}
                  />
                ) : undefined
              }
              freezeLine
            />
            <ChildEntitiesActions
              store={store}
              disabled={disabled}
              editorInfo={editorInfo}
            />
            <ChildEntitiesCreators store={store} disabled={disabled} />
          </div>
        </div>

        <div
          className={classNames([
            styles.tableContainer,
            [isHeightLimit, styles.heightLimited],
          ])}
          ref={tableRef}
        >
          <AsyncTableView<ZEntityRow, ZEntityFiltersChild>
            store={tableStore}
            columns={tableStore.columns}
            onRowClick={onRow}
            useHeader
            usePagination
            useSelection={useSelection}
            useFooter={useFooter}
            templateToken={childEntsTemplateToken}
          />
        </div>
        <EntityEditorModal
          store={subCardStore}
          modalProps={{
            open: store.isPopup,
            onCancel: () => {
              subCardStore?.safeAction(() => store.closeCreation());
            },
          }}
          editorProps={{
            submitText: store.submitText,
            onSuccess: () => {
              store.onUpdate();
            },
            onDelete: () => {
              store.doDeleteSubEntity().catch(onError);
            },
            deleting: store.deletingSubEntity,
          }}
        />
      </div>
    );
  },
);
